import { Segmented } from 'antd';
import { BranchSelectField } from '../CustomerSidebar/BranchSelectField';
import { FULFILLMENT_CHOICES, FULFILLMENT_LABEL, type FulfillmentChoice } from './fulfillmentChoice';

interface Props {
  value: FulfillmentChoice;
  onChange: (c: FulfillmentChoice) => void;
  /**
   * Chỉ xem (màn sửa đơn, đơn đã chốt giỏ): khoá cả thanh VÀ bỏ ô chọn chi
   * nhánh. Ô đó là `BranchSelectField` — một `Form.Item` bind `pickup_store_id`;
   * bên `Orders/edit.tsx` nhóm field vận chuyển KHÔNG được đăng ký vào Form
   * (payload đơn đã chốt dựa đúng vào giả định "không có Form.Item ⇒ không gửi").
   * Khoá mà vẫn mount ô đó là vẫn đăng ký field.
   */
  disabled?: boolean;
}

/**
 * Thanh phương thức nhận hàng — neo đáy cột hàng, là NƠI DUY NHẤT trên màn POS
 * chọn được phương thức nhận hàng.
 *
 * Trước đây khối này render ở ba nơi (panel Thông tin đơn hàng, Drawer thanh toán,
 * Dialog khách hàng) và cả ba cùng bind Form.Item `pickup_store_id`, nên phải có
 * cờ `showBranchField` để nhường ô cho nhau — hai ô required trùng tên sinh hai
 * dòng lỗi khi validate fail. Render một chỗ thì cả cơ chế đó không cần nữa.
 *
 * `pickup` KHÔNG còn ô chọn chi nhánh ở đây nữa — chi nhánh nhận hàng luôn LÀ chi
 * nhánh thu ngân đang mở ca, `FulfillmentFormSection` tự ép `pickup_store_id`
 * bằng Form.Item ẩn, không cần thu ngân chọn tay.
 *
 * `vault` (đã ẩn khỏi thanh 4 nút, chỉ đơn khôi phục cũ còn mang giá trị này) vẫn
 * giữ ô chọn tay như trước, dùng `BranchSelectField`, TUYỆT ĐỐI KHÔNG dùng
 * `StoreBranchField`. Hai cái tên gần giống nhau nhưng làm hai việc khác hẳn:
 *  - `BranchSelectField` bind Form field `pickup_store_id` — nơi KHÁCH nhận hàng.
 *  - `StoreBranchField` bind `useStoreSelection()` / `pos.shiftStoreId` — chi nhánh
 *    mà THU NGÂN đang mở ca. Đổi nó nghĩa là chuyển cả ca bán hàng sang chi nhánh
 *    khác.
 * Dùng nhầm thì thu ngân tưởng đang chọn nơi khách lấy hàng, thực ra đang đổi chi
 * nhánh bán của chính mình — đơn sẽ ghi sai cửa hàng và tồn kho trừ nhầm nơi.
 *
 * Đồng bộ `pickup_store_id` theo chi nhánh đang bán là việc của
 * `FulfillmentFormSection` — pickup đồng bộ MỌI LÚC, vault chỉ đồng bộ một chiều
 * `shiftStoreId → pickup_store_id` khi chi nhánh đang bán THẬT SỰ đổi.
 *
 * Vì `BranchSelectField` tự bind Form, component này phải nằm trong một antd Form.
 * Không thêm lối thoát không-Form.
 */
export function FulfillmentBar({ value, onChange, disabled }: Props): JSX.Element {
  /*
    'Lưu kho' đã gỡ khỏi `FULFILLMENT_CHOICES` — không tạo đơn mới kiểu đó nữa
    — nhưng đơn CŨ (và snapshot khôi phục) vẫn mang giá trị này. Thiếu nút thì
    thanh không nút nào sáng: người xem không biết đơn đang ở hình thức gì, mà
    đó đúng là thông tin thanh này sinh ra để nói. Nút thêm lại LUÔN khoá — hiện
    sự thật của đơn cũ, không mở lại đường tạo đơn lưu kho mới.
  */
  const options =
    FULFILLMENT_CHOICES.some((c) => c.value === value)
      ? FULFILLMENT_CHOICES
      : [...FULFILLMENT_CHOICES, { value, label: FULFILLMENT_LABEL[value], disabled: true }];

  return (
    <div
      className="pos-fulfillment-bar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 12px',
        borderTop: '1px solid #e0e0e0',
        background: '#fafafa',
        flexShrink: 0,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <Segmented
          block
          size="small"
          className="pos-segmented-selected-red"
          value={value}
          disabled={disabled}
          // Bấm lại nút đang chọn: antd Segmented không phát onChange, nên không
          // cần chặn thêm. Giữ ép kiểu ở đúng một chỗ này.
          onChange={(v) => onChange(v as FulfillmentChoice)}
          options={options}
        />
      </div>

      {value === 'vault' && !disabled && (
        <div className="pos-fulfillment-bar-branch" style={{ width: 240, flexShrink: 0 }}>
          <BranchSelectField label="Chi nhánh lưu kho" />
        </div>
      )}
    </div>
  );
}
